import React, { useState, useMemo, useEffect } from "react";
import Select from "../Select";
import { Empty } from "antd";
import { SelectProps } from "antd/lib/select";
import { debounce, find, get, isEmpty } from "lodash";
import compact from "lodash/compact";
import useSWR from "swr";
import { useTranslation } from "react-i18next";
import { IBank } from "./services/typings";
import registerService from "pages/Auth/pages/Register/services/registerService";
import { useToken } from "pages/Auth/hooks";

interface IProps extends SelectProps<any> {}

const BankSelect = (props: IProps) => {
  const { value, onChange, ...rest } = props;
  const token = useToken();
  const [searchText, setSearchText] = useState("");

  const { data, error } = useSWR(
    token ? ["banks"] : null,
    () => registerService.getBank({ token }),
    {
      shouldRetryOnError: false,
      refreshWhenHidden: false,
      revalidateOnFocus: false,
      refreshWhenOffline: false,
      revalidateOnReconnect: false,
    }
  );

  useEffect(() => {
    if (value && !isEmpty(data)) {
      const bank = find(data, (b) => b.id === value.id);

      if (!bank && onChange) {
        onChange(undefined, { value: "" });
      }
    }
  }, [data, onChange, value]);

  const items = useMemo(() => data || [], [data]);
  const loading = data === undefined && !error;

  const val = useMemo(() => get(value, "id") || get(value, "name"), [value]);

  const filteredItems = useMemo(() => {
    const s = searchText.toLowerCase();
    return compact([
      ...(items || []),
      items.length === 0 ? value : undefined,
    ]).filter(
      (bank: IBank) =>
        !searchText ||
        bank?.bank_name.toLowerCase()?.includes(s) ||
        bank?.short_name?.toLowerCase()?.includes(s)
    );
  }, [items, searchText, value]);

  const debouncedSetSearchText = useMemo(
    () => debounce(setSearchText, 500),
    [setSearchText]
  );

  const handleOnChange = (val) => {
    const item = find(filteredItems, (i) => i.id == val);
    if (onChange && item) {
      onChange(item, { value: item });
    }
  };

  const { t } = useTranslation();

  return (
    <Select
      style={{ display: "flex", flex: 1 }}
      placeholder={t("contract.bank_and_branch")}
      loading={loading}
      data={filteredItems}
      onSearch={debouncedSetSearchText}
      notFoundContent={loading ? null : <Empty />}
      getDisplayText={(val: IBank) => `${val.bank_name} (${val.short_name})`}
      getValue={(val: IBank) => val.id}
      showSearch
      filterOption={false}
      value={val}
      onChange={handleOnChange}
      {...rest}
    />
  );
};

export default BankSelect;
